/**
 * Words and small helpers shared by Import History and an import's own page.
 */
import type { ImportAction, ImportRunRow } from '@mouldtrack/shared';
import type { BadgeTone } from '../../components/ui/primitives.js';

export const RUN_STATUS: Record<ImportRunRow['status'], { tone: BadgeTone; label: string }> = {
  COMPLETED: { tone: 'success', label: 'Completed' },
  COMPLETED_WITH_ERRORS: { tone: 'warning', label: 'Completed, with problems' },
  FAILED: { tone: 'danger', label: 'Failed - nothing written' },
  RUNNING: { tone: 'info', label: 'In progress' },
};

/** What a sheet row became, in the words the import's check screen uses. */
export const RECORD_ACTION: Record<ImportAction, { tone: BadgeTone; label: string; meaning: string }> = {
  create: { tone: 'success', label: 'New', meaning: 'Added to the catalog by this import.' },
  restore: { tone: 'primary', label: 'Brought back', meaning: 'A deleted mould this import brought back.' },
  update: { tone: 'info', label: 'Filled in', meaning: 'Already in the catalog; this import filled in or updated values.' },
  unchanged: { tone: 'neutral', label: 'Already in', meaning: 'Already in the catalog; the sheet added nothing new.' },
  blocked: { tone: 'danger', label: 'Not imported', meaning: 'This row did not go in. The reason is shown with it.' },
};

/** "MOLD LIST_63.xlsx" -> "XLSX". */
export function fileType(fileName: string): string {
  const ext = /\.([A-Za-z0-9]+)$/.exec(fileName)?.[1];
  return ext ? ext.toUpperCase() : 'FILE';
}

/** Rows that went in, and rows that did not. */
export function runTotals(run: ImportRunRow): { imported: number; notImported: number; added: number } {
  return {
    imported: run.created + run.restored + run.updated + run.unchanged,
    notImported: run.skipped + run.failed,
    added: run.created + run.restored,
  };
}

/** A date input's day as the first or last moment of that day here, for the server. */
export function dayToIso(day: string, end = false): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined;
  const d = new Date(`${day}T${end ? '23:59:59.999' : '00:00:00.000'}`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** A spreadsheet-friendly CSV: quoted where needed, with the mark Excel needs to read UTF-8. */
export function downloadCsv(fileName: string, rows: Array<Array<string | number | null | undefined>>): void {
  const cell = (v: string | number | null | undefined) => {
    const text = v == null ? '' : String(v);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const csv = rows.map((r) => r.map(cell).join(',')).join('\r\n');
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** "MOLD LIST_63.xlsx" -> "MOLD LIST_63", for naming what is downloaded from it. */
export function baseName(fileName: string): string {
  return fileName.replace(/\.[A-Za-z0-9]+$/, '').replace(/[\\/:*?"<>|]+/g, '-').trim() || 'import';
}
